// The Suggestions tab (docs/ai-connections-brief.md, Part 2 outcome c), in the format of the 2206 time-standard
// review page: rows grouped by job item, each with what changes, the new value in a box Spencer may edit, the
// reason and the source, a narrow "What it was" column, and two buttons: Approve as shown (applies exactly what
// is in the box) and Send back (returns it with his note). A row whose target no longer holds "What it was"
// says so and cannot be approved until it is suggested again. The job shows "price if all approved" beside its
// price: the engine run over the job with every waiting suggestion laid over it, never stored. Every decided
// row stays below as the job's history.
//
// For Spencer (2026-10-17): what an AI employee hands him that a suggestion cannot reach (the job's percentages, the
// large-purchase switch, a build, publishing, verifying, a call only he can make) sits in its own panel above the
// suggestions: the item, what to do, why, a link to the exact place, and where the employee sent the change itself,
// the price and the margin both ways, worked out live and never stored. He ticks Done or Dismiss, each with an
// optional note the employee reads back.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { applySuggestions, applyWhatIf, libraryWith, resolveJob, type Resolution, type SuggestionIn, type WhatIf } from '@zerogap/engine';
import { refresh, rpc, toast, useRows, type Row } from '../../lib/api';
import { CATALOG_KIND_WORDS, COLUMN_WORDS, approveSuggestion, approvedWords, boxValue, isCatalogKind, isScopeKind, sayError, sendBackSuggestion } from '../../lib/suggest';
import { pricingOptions } from '../../lib/job';
import { longDate } from '../../lib/format';
import { useSession } from '../../lib/session';
import { Btn, Empty, Info, Money, Panel, Tag } from '../../ui/kit';
import type { JobCtx } from './Workspace';

const KIND_WORDS: Record<string, string> = {
  add_job_item: 'Add an item',
  change_input: 'Change an input',
  set_job_standard: 'Set a job standard',
  type_price: 'Type a vendor price',
  sub_line_add: 'Add a sub line',
  sub_line_edit: 'Edit a sub line',
  move_item: 'Move into an add-on',
  create_add_on: 'Create an add-on',
  custom_line: 'Custom line',
  item_route_step: 'Route step on this item',
  item_bom_line: 'Material line on this item',
  hold_value: 'Hold a value on this item',
  new_material: 'New material for the library',
  job_route_step: 'Job route step',
  job_bom_line: 'Job BOM line',
  job_cost_line: 'Job-wide cost',
  scope_reword: 'Scope row reworded',
  scope_link: 'Scope row linked to its item',
  scope_add: 'Scope row added',
  scope_dismiss: 'Scope row dismissed',
  library_recipe_line: 'Recipe line formula',
  library_operation: 'Operation',
  library_time_standard: 'Time standard',
  library_route_step: 'Recipe route step',
  library_bom_line: 'Recipe material line',
  library_part: 'Recipe part',
  library_input: 'Catalog input',
  library_calculation: 'Catalog calculation',
  library_child: 'Child it makes',
};

/** A new catalog row in words: what the suggestion's row holds beside the main formula in the box. */
const catalogRowWords = (kind: string, row: Record<string, unknown>, p: Record<string, unknown>): string => {
  const parts: string[] = [];
  if (kind === 'library_route_step') parts.push(String(p.operation_name ?? ''), row.charge_setup === 'false' ? 'no setup' : '');
  if (kind === 'library_bom_line') parts.push(String(p.material_name ?? row.label ?? row.material_role ?? ''), row.unit_price_expr ? `price ${String(row.unit_price_expr)}` : '');
  if (kind === 'library_part') parts.push(String(row.name ?? ''), `${String(row.length_expr ?? '0')} × ${String(row.width_expr ?? '0')}`, String(p.material_name ?? row.material_role ?? ''));
  if (kind === 'library_input') parts.push(String(row.key ?? ''), String(row.data_type ?? 'number'), row.unit ? String(row.unit) : '', row.choices ? (row.choices as unknown[]).join(' | ') : '', row.standard_key ? `follows ${String(row.standard_key)}` : '');
  if (kind === 'library_calculation') parts.push(String(row.key ?? ''), row.label && row.label !== row.key ? `(${String(row.label)})` : '');
  if (kind === 'library_child') parts.push(String(row.label ?? p.child_name ?? 'child'), String(p.child_name ?? row.child_expr ?? ''), `× ${String(row.count_expr ?? '')}`);
  return parts.filter(Boolean).join(' · ');
};

const show = (v: unknown): string => (v === null || v === undefined || v === '' ? '(blank)' : typeof v === 'object' ? JSON.stringify(v) : String(v));
/** The same value, as ug.same_value reads it: two numbers as numbers (898 and 898.00), else the words (2026-10-25). */
const sameValue = (a: unknown, b: unknown): boolean => {
  const x = show(a);
  const y = show(b);
  const n = /^-?d+(.d+)?$/;
  return x === y || (n.test(x) && n.test(y) && Number(x) === Number(y));
};

/** A custom line in words: what it is, how many at what, from whom, when. */
const lineWords = (l: Record<string, unknown> | undefined, unitCost: unknown) =>
  l ? `"${String(l.label ?? '')}", ${String(l.qty ?? 1)} × $${show(unitCost ?? l.unit_cost)}${l.vendor ? ` from ${String(l.vendor)}` : ''}${l.quote_date ? `, ${String(l.quote_date)}` : ''}` : '';

/** What a suggestion changes, in words, from the rows it points at. */
export function whatChanges(s: Row, numberOf: (id: unknown) => string | null, labelOf: (itemId: unknown, key: string) => string | null, addOnName: (id: unknown) => string | null = () => null, inGroup = false): string {
  const p = (s.payload ?? {}) as Record<string, unknown>;
  // under its own heading (Job route, Job-wide costs) a row does not repeat the heading (Iris, 2026-10-19); the history, ungrouped, keeps it
  const lead = (words: string) => (inGroup ? '' : `${words} · `);
  switch (s.kind) {
    case 'create_add_on': {
      const moves = ((p.move_numbers as string[] | undefined) ?? []).join(', ');
      const alts = ((p.alternates as Row[] | undefined) ?? []).map((a) => `${a.number} for ${a.replaces_number ?? numberOf(a.replaces_item_id) ?? '?'}`).join(', ');
      return `New add-on, unpublished${moves ? ` · moves ${moves} out of the base` : ''}${alts ? ` · alternates ${alts}` : ''}`;
    }
    case 'custom_line':
      return s.target_id
        ? `${numberOf(s.target_id) ?? 'custom item'} · fill with ${lineWords(p.line as Record<string, unknown>, s.new_value)}`
        : `Add ${String(p.number ?? '')}${p.parent_item_id ? ` under ${numberOf(p.parent_item_id) ?? 'its item'}` : ''} · ${lineWords(p.line as Record<string, unknown>, s.new_value)}`;
    case 'add_job_item':
      return `Add ${String(p.number ?? '')}${p.name ? ` · ${String(p.name)}` : ''}${p.inputs && typeof p.inputs === 'object' ? ` (${Object.entries(p.inputs as Record<string, unknown>).map(([k, v]) => `${k} ${show(v)}`).join(', ')})` : ''}`;
    case 'item_route_step':
    case 'item_bom_line': {
      // a step or line on this job item alone (2026-10-18): what it does, to what, on a catalog item "beside its recipe"
      const name = String(p.operation_name ?? p.material_name ?? '');
      const unit = p.unit ? ` ${String(p.unit)}` : '';
      const what = s.kind === 'item_route_step' ? 'step' : 'line';
      const where = p.step_id || p.line_id ? ' (a recipe row, changed on this item only)' : '';
      if (p.change === 'add') return `${numberOf(s.target_id) ?? 'item'} · add ${what} ${name} × ${show(s.new_value)}${unit}${p.material_suggestion_id ? ' (a new material, suggested beside it)' : ''}`;
      if (p.change === 'remove') return `${numberOf(s.target_id) ?? 'item'} · remove ${what} ${String(p.was ?? name)}${where}`;
      return `${numberOf(s.target_id) ?? 'item'} · change ${what} ${String(p.was ?? name)} → ${name} × ${show(s.new_value)}${unit}${where}`;
    }
    case 'hold_value':
      return `${numberOf(s.target_id) ?? 'item'} · hold ${labelOf(s.target_id, String(s.target_key)) ?? s.target_key} on this item`;
    // the job's own hours and material lines (2026-10-19): a quantity typed against a step of the job type's route, a step added or removed
    case 'job_route_step':
    case 'job_bom_line': {
      const name = String(p.operation_name ?? p.material_name ?? '');
      const unit = p.unit ? ` ${String(p.unit)}` : '';
      const what = s.kind === 'job_route_step' ? 'step' : 'line';
      const where = lead(s.kind === 'job_route_step' ? 'Job route' : 'Job BOM');
      const ofType = !!(p.type_step_id || p.type_line_id);
      if (p.change === 'add') return `${where}add ${what} ${name} × ${show(s.new_value)}${unit} (the job type does not list it)`;
      if (p.change === 'remove') return `${where}remove ${name}${ofType ? ` (a ${what} of the job type: this job types 0 against it)` : ''}`;
      const was = s.old_value === null || s.old_value === undefined || s.old_value === '' ? 'not set' : show(s.old_value);
      return `${where}${name} · ${was} → ${show(s.new_value)}${unit}`;
    }
    // a job-wide cost (2026-10-19): tax, shipping, handling on a vendor order, the Price tab's Job-wide cost rows
    case 'job_cost_line': {
      const line = (p.line ?? {}) as Record<string, unknown>;
      const where = lead('Job-wide cost');
      // Separate pricing (2026-10-22): its own markup (blank = the company's), outside the margin when it ignores it
      const markup = line.markup_pct === null || line.markup_pct === undefined || line.markup_pct === '' ? 'company' : `${String(line.markup_pct)}%`;
      const pricing = line.separate_pricing === true ? ` · separate pricing, ${markup} markup${line.ignore_margin === true ? ', no margin' : ''}` : '';
      if (p.change === 'add') return `${where}add "${String(line.label ?? '')}" $${show(s.new_value)}${pricing}`;
      if (p.change === 'remove') return `${where}remove ${String(p.was ?? line.label ?? '')}`;
      return `${where}${String(line.label ?? p.was ?? '')} · $${show(s.old_value)} → $${show(s.new_value)}${line.separate_pricing === false ? ' · standard pricing' : pricing}`;
    }
    // a scope row (2026-10-18): what the client gets, in words; these wait on the Scope tab and are history here
    case 'scope_reword':
      return `Scope row "${show(s.old_value)}" → "${show(s.new_value)}"`;
    case 'scope_link':
      return `Scope row → ${p.whole_job ? 'the whole job' : Array.isArray(p.item_numbers) ? (p.item_numbers as unknown[]).join(', ') : String(p.item_number ?? show(s.new_value))} (was ${show(s.old_value)})`;
    case 'scope_add':
      return `New scope row "${show(s.new_value)}"${p.whole_job ? ' · the whole job' : Array.isArray(p.item_numbers) && (p.item_numbers as unknown[]).length ? ` → ${(p.item_numbers as unknown[]).join(', ')}` : p.item_number ? ` → ${String(p.item_number)}` : p.dismiss_reason ? ' · dismissed' : ' · pending'}`;
    case 'scope_dismiss':
      return `Scope row dismissed: ${show(s.new_value)}`;
    case 'new_material':
      // since 2026-10-19 a pack is filed as the pack's price with its size; one filed before carries the price per piece
      return p.priced_per_pack === true
        ? `New material "${String(p.description ?? '')}" · $ per pack of ${show(p.pack_size)} (${String(p.unit ?? 'ea')}), ${p.whole_packs === true ? 'a job pays for whole packs' : 'a job pays for the pieces it uses'}${p.vendor ? ` · from ${String(p.vendor)}` : ''}${p.link ? ' · link' : ''}`
        : `New material "${String(p.description ?? '')}" · $ per ${String(p.unit ?? 'ea')}${p.vendor ? ` from ${String(p.vendor)}` : ''}${p.pack_size ? `, ordered ${show(p.pack_size)} at a time` : ''}${p.link ? ' · link' : ''}`;
    // a sub line an AI employee adds or edits (2026-10-25)
    case 'sub_line_add':
      return `New sub line "${show(s.new_value)}"${Array.isArray(p.said) && (p.said as unknown[]).length ? ` · ${(p.said as unknown[]).join(', ')}` : ''}`;
    case 'sub_line_edit':
      if (s.target_key === 'builds') return `Sub line · in ${String(p.was ?? '')} → in ${String(p.build_words ?? '')}`;
      if (s.target_key === 'build_amount') return `Sub line · under ${String(p.build_name ?? 'a build')}: ${s.old_value === null ? "the line's own number" : `${show(s.old_value)}`} → ${s.new_value === null ? "the line's own number" : `${show(s.new_value)}`}`;
      return `Sub line · ${String(s.target_key ?? '').replace(/_id$/, '').replace(/_/g, ' ')}: ${String(p.was ?? show(s.old_value))} → ${String(p.words ?? show(s.new_value))}`;
    case 'change_input':
    case 'type_price':
      if (s.target_table === 'sub_line') return `Sub line · ${s.target_key === 'quote_amount' ? 'quote' : 'internal estimate'}`;
      return `${numberOf(s.target_id) ?? 'item'} · ${labelOf(s.target_id, String(s.target_key)) ?? s.target_key}`;
    case 'set_job_standard':
      return `Job standard · ${s.target_key}`;
    case 'move_item':
      return `${numberOf(s.target_id) ?? 'item'} into the add-on ${addOnName(p.add_on_id) ?? ''} (out of the base)`;
    // a catalog item's own row (2026-10-18): the recipe's step, line or part, the item's input or calculation; add, change or remove
    case 'library_route_step':
    case 'library_bom_line':
    case 'library_part':
    case 'library_input':
    case 'library_calculation': {
      const what = CATALOG_KIND_WORDS[String(s.kind)] ?? 'Row';
      if (p.change === 'add') return `Add ${what.toLowerCase()} ${catalogRowWords(String(s.kind), (p.row ?? {}) as Record<string, unknown>, p)}`;
      if (p.change === 'remove') return `Retire ${what.toLowerCase()} ${String(p.was ?? p.row_name ?? '')} (behind Show retired, with Restore)`;
      if (p.change === 'restore') return `Restore ${what.toLowerCase()} ${String(p.was ?? p.row_name ?? '')}`;
      return `${what} ${String(p.row_name ?? '')} · ${COLUMN_WORDS[String(s.target_key)] ?? s.target_key}`;
    }
    // the twins of the screens' writes (2026-10-23)
    case 'create_job':
      return `New job "${show(s.new_value)}" on ${String(p.opportunity_name ?? 'the opportunity')}${p.job_type_name ? ` (${String(p.job_type_name)})` : ''}`;
    case 'edit_item':
      return `${numberOf(s.target_id) ?? 'item'} · ${String(p.field ?? s.target_key)}: ${show(s.old_value)} → ${show(s.new_value)}`;
    case 'retire_item':
      return `${numberOf(s.target_id) ?? String(p.number ?? 'item')} · retire (out of every price, behind Show retired, with Restore)`;
    case 'restore_item':
      return `${numberOf(s.target_id) ?? String(p.number ?? 'item')} · restore`;
    case 'room_add':
      return `Add the room ${show(s.new_value)}${Array.isArray(p.item_numbers) && (p.item_numbers as unknown[]).length ? ` with ${(p.item_numbers as unknown[]).join(', ')}` : ''}`;
    case 'room_rename':
      return `Room ${show(s.old_value)} → ${show(s.new_value)}`;
    case 'room_standard':
      return `Room ${String(p.room_name ?? '')} · ${s.target_key}: ${show(s.old_value)} → ${s.new_value === null ? 'follows the job' : show(s.new_value)}`;
    case 'edit_job':
      return `${lead('Job setup')}${String(p.field ?? s.target_key).replace(/_/g, ' ')}: ${show(s.old_value)} → ${show(s.new_value)}`;
    case 'job_pricing':
      return `${lead('Percentages')}${Object.entries((p.percentages ?? {}) as Record<string, unknown>).map(([k, v]) => `${k} ${v === null ? 'follows the company' : `${String(v)}%`}`).join(', ')}`;
    case 'edit_custom_line':
      return `${numberOf(s.target_id) ?? 'item'} · typed line ${String(p.was ?? '')} → ${lineWords({ ...((p.line ?? {}) as Record<string, unknown>) }, s.new_value ?? undefined)}`;
    case 'build_create':
      return `New build "${show(s.new_value)}", unpublished${Object.keys((p.standards ?? {}) as object).length ? ` · ${Object.entries((p.standards ?? {}) as Record<string, unknown>).map(([k, v]) => `${k} ${show(v)}`).join(', ')}` : ''}`;
    case 'build_edit':
      return `Build ${String(p.build_name ?? '')}${p.name ? ` → ${String(p.name)}` : ''}${Object.keys((p.standards ?? {}) as object).length ? ` · ${Object.entries((p.standards ?? {}) as Record<string, unknown>).map(([k, v]) => `${k} ${v === null ? 'follows the job' : show(v)}`).join(', ')}` : ''}${Object.keys((p.percentages ?? {}) as object).length ? ` · ${Object.entries((p.percentages ?? {}) as Record<string, unknown>).map(([k, v]) => `${k} ${v === null ? 'follows the job' : `${String(v)}%`}`).join(', ')}` : ''}`;
    case 'package_create':
      return `New package "${show(s.new_value)}"`;
    case 'package_edit':
      return `Package ${String(p.package_name ?? '')}${p.name ? ` → ${String(p.name)}` : ''}${p.add_on_ids ? ' · its add-ons' : ''}${p.applies_to ? ' · its builds' : ''}${Object.keys((p.percentages ?? {}) as object).length ? ' · margins' : ''}`;
    case 'add_on_edit':
      return `Add-on ${String(p.add_on_name ?? '')}${p.name ? ` → ${String(p.name)}` : ''}${p.applies_to ? ' · its builds' : ''}${Object.keys((p.percentages ?? {}) as object).length ? ' · margins' : ''}`;
    case 'scope_source':
      return `Scope row · source → ${String(p.source_title ?? '(none)')}`;
    case 'scope_tag':
      return `Scope row · tagged to ${Array.isArray(p.tag_words) && (p.tag_words as unknown[]).length ? (p.tag_words as unknown[]).join(', ') : 'nothing'}`;
    case 'scope_outcome':
      return `Scope row · ${String(p.outcome)}${p.outcome === 'item' && Array.isArray(p.item_numbers) ? `: ${(p.item_numbers as unknown[]).join(', ')}` : p.outcome === 'dismissed' ? `: ${show(s.new_value)}` : ''} (was ${show(s.old_value)})`;
    case 'mark_place':
      return `${numberOf(s.target_id) ?? 'item'} · ${p.change === 'add' ? 'mark placed' : 'mark moved'} on ${String(p.view_name ?? 'the View')} at ${String(p.x_pct)}% across, ${String(p.y_pct)}% down`;
    case 'mark_remove':
      return `${numberOf(s.target_id) ?? 'item'} · mark removed from ${String(p.view_name ?? 'the View')}`;
    case 'library_material':
      return `Material ${String(p.material_name ?? '')} · ${p.change === 'retire' ? 'retire' : p.change === 'restore' ? 'restore' : `${s.target_key}: ${show(s.old_value)} → ${show(s.new_value)}`}`;
    case 'library_item':
      return p.change === 'create' ? `New catalog item "${show(s.new_value)}"${p.family ? ` in ${String(p.family)}` : ''}` : `Catalog item ${String(p.item_name ?? '')} · ${p.change === 'retire' ? 'retire' : p.change === 'restore' ? 'restore' : `${s.target_key}: ${show(s.old_value)} → ${show(s.new_value)}`}`;
    case 'library_recipe_line':
      return `${s.target_table === 'recipe_bom_line' ? 'Material line' : 'Route step'} · ${s.target_key}`;
    case 'library_operation':
      return `Operation · ${s.target_key}`;
    case 'library_time_standard':
      return `Time standard · minutes per unit${p.setup_minutes !== undefined ? `, setup ${show(p.setup_minutes)}` : ''}`;
    default:
      return String(s.kind);
  }
}

/** The rows, grouped, with the boxes and the two buttons; shared by the job's tab and the Library's queue. */
export function SuggestionRows({
  rows,
  users,
  canDecide,
  groupOf,
  numberOf,
  labelOf,
  currentOf,
  addOnName,
  detailOf,
  hrefOf,
  widths = ['24%', '14%', '30%', '12%', '20%'],
}: {
  rows: Row[];
  users: Row[];
  canDecide: boolean;
  groupOf: (s: Row) => string;
  numberOf: (id: unknown) => string | null;
  labelOf: (itemId: unknown, key: string) => string | null;
  addOnName?: (id: unknown) => string | null;
  /** The target's value now, as text, or undefined when the screen cannot tell (the database still checks on approve). */
  currentOf: (s: Row) => string | null | undefined;
  /** A line under what changes: what an alternate changes on the item it replaces. */
  detailOf?: (s: Row) => string | null;
  /** Where a group's heading opens (the catalog editor on the recipe a library suggestion changes). */
  hrefOf?: (s: Row) => string | null;
  /** The five column widths; the Library's queue gives the new value and what it was more room (a note, a formula). */
  widths?: [string, string, string, string, string];
}) {
  const [boxes, setBoxes] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const who = (id: unknown) => users.find((u) => u.id === id)?.name ?? '—';
  const groups = new Map<string, Row[]>();
  for (const s of rows) groups.set(groupOf(s), [...(groups.get(groupOf(s)) ?? []), s]);
  const idColumn = (s: Row) => s.target_key === 'operation_id' || s.target_key === 'material_id';

  const approve = async (s: Row) => {
    setBusy(s.id);
    try {
      await approveSuggestion(s, boxValue(s, boxes[s.id]), notes[s.id]?.trim() || null);
      toast(approvedWords(s));
    } catch (e) {
      sayError(e);
    } finally {
      setBusy(null);
    }
  };
  const sendBack = async (s: Row) => {
    setBusy(s.id);
    try {
      await sendBackSuggestion(s, notes[s.id] ?? '');
      toast('Sent back.');
    } catch (e) {
      sayError(e);
    } finally {
      setBusy(null);
    }
  };

  if (rows.length === 0) return <Empty>Nothing waiting.</Empty>;
  return (
    <div className="stack">
      {[...groups.entries()].map(([name, list]) => (
        <div key={name}>
          <b>{hrefOf?.(list[0]!) ? <Link to={hrefOf(list[0]!)!}>{name}</Link> : name}</b>
          <div className="tblwrap">
            <table className="fixed suggest">
              <colgroup>
                {widths.map((w, k) => (
                  <col key={k} style={{ width: w }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th>What changes</th>
                  <th>New value</th>
                  <th>Reason · source</th>
                  <th>What it was</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((s) => {
                  const current = currentOf(s);
                  const p = (s.payload ?? {}) as Record<string, unknown>;
                  const catalog = isCatalogKind(s.kind);
                  // a line kind: a step or line on one item (2026-10-18), the job route, the job BOM or a job-wide cost (2026-10-19)
                  const lineKind = s.kind === 'item_route_step' || s.kind === 'item_bom_line' || s.kind === 'job_route_step' || s.kind === 'job_bom_line' || s.kind === 'job_cost_line';
                  const adds = s.kind === 'add_job_item' || ((lineKind || catalog) && p.change === 'add') || (s.kind === 'custom_line' && !!s.target_id);
                  const removes = (lineKind || catalog) && p.change === 'remove';
                  const stale = !adds && current !== undefined && !sameValue(current, s.old_value);
                  // an operation or a material is picked by name, not typed: the box shows the name and is not edited here
                  const noBox = removes || (catalog && idColumn(s));
                  // the unit in Spencer's words (Iris, 2026-10-19): a per-job step is typed in hours, travel in minutes, the rest in the operation's unit
                  const unitWord = p.unit === '/hr' ? 'hours' : p.unit === '/min' ? 'minutes' : p.unit ? `how many · ${String(p.unit)}` : 'how many';
                  const boxWord = s.kind === 'create_add_on' ? 'its name' : s.kind === 'custom_line' ? '$ each' : s.kind === 'job_cost_line' ? '$' : lineKind ? unitWord : s.kind === 'new_material' ? `$ per ${String(p.unit ?? 'ea')}` : catalog && s.target_key ? COLUMN_WORDS[String(s.target_key)] ?? String(s.target_key) : null;
                  // a job-wide row reads the job type's starting quantity until the job types its own (Iris, 2026-10-19: say which, under what it was)
                  const readsType = (s.kind === 'job_route_step' || s.kind === 'job_bom_line') && !!(p.type_step_id || p.type_line_id) && !p.line_id && p.change !== 'add';
                  const box = boxes[s.id] ?? (s.new_value === null || s.new_value === undefined ? '' : typeof s.new_value === 'object' ? JSON.stringify(s.new_value) : String(s.new_value));
                  return (
                    <tr key={s.id}>
                      <td>
                        {whatChanges(s, numberOf, labelOf, addOnName, true)}
                        {detailOf?.(s) ? (
                          <div className="muted">
                            <small>{detailOf(s)}</small>
                          </div>
                        ) : (
                          <br />
                        )}
                        <small>
                          {KIND_WORDS[String(s.kind)] ?? s.kind} · {who(s.suggested_by)}, {longDate(s.suggested_at)}
                        </small>
                      </td>
                      <td data-label="New value">
                        {s.kind === 'add_job_item' || noBox ? (
                          <small>{removes ? 'removed' : catalog ? String(p.operation_name ?? p.material_name ?? '(none)') : 'as described'}</small>
                        ) : (
                          <>
                            {s.kind === 'create_add_on' || catalog ? (
                              // a name, a note or a formula is never cut off: the box wraps it
                              <textarea className="cell" rows={catalog ? 3 : 2} value={box} disabled={!canDecide || stale} onChange={(e) => setBoxes({ ...boxes, [s.id]: e.target.value })} style={{ width: '100%', resize: 'vertical' }} />
                            ) : (
                              <input className="cell" value={box} disabled={!canDecide || stale} onChange={(e) => setBoxes({ ...boxes, [s.id]: e.target.value })} style={{ width: '100%' }} />
                            )}
                            {boxWord && <small>{boxWord}</small>}
                          </>
                        )}
                      </td>
                      <td data-label="Reason · source">
                        {s.reason}
                        <br />
                        <small>
                          {/^https?:\/\//.test(String(s.source)) ? (
                            <a href={String(s.source)} target="_blank" rel="noreferrer">
                              {String(s.source).replace(/^https?:\/\//, '').slice(0, 60)}
                            </a>
                          ) : (
                            String(s.source)
                          )}
                          {s.source_vendor ? ` · ${s.source_vendor}, ${s.source_date}` : ''}
                        </small>
                      </td>
                      <td className="muted" data-label="What it was">
                        {s.kind === 'custom_line' || adds || s.kind === 'new_material' ? (s.kind === 'new_material' ? 'not in the library' : 'new') : s.kind === 'create_add_on' ? 'none' : (lineKind && p.was) || (catalog && (removes || idColumn(s)) && p.was) ? String(p.was).replace(/× blank\b/, '× not set') : s.kind === 'hold_value' ? (s.old_value === null || s.old_value === undefined ? 'follows the standard' : show(s.old_value)) : Array.isArray(s.old_value) ? s.old_value.join(' | ') : show(s.old_value)}
                        {readsType && (
                          <div>
                            <small>{s.old_value === null || s.old_value === undefined || s.old_value === '' ? 'nothing typed on this job yet' : "the job type's starting quantity; nothing typed on this job yet"}</small>
                          </div>
                        )}
                        {stale && (
                          <div className="warn">
                            {s.kind === 'create_add_on' ? show(current) : s.kind === 'new_material' ? `the library has ${show(current)}` : `now ${show(current)}`}: suggest it again
                          </div>
                        )}
                        {s.kind === 'item_bom_line' && !!p.material_suggestion_id && (
                          <div>
                            <small>Approve its new material first (under Library).</small>
                          </div>
                        )}
                      </td>
                      <td>
                        {canDecide ? (
                          <div className="stack" style={{ gap: 4 }}>
                            <Btn small primary disabled={busy === s.id || stale} onClick={() => void approve(s)}>
                              Approve as shown
                            </Btn>
                            <Btn small disabled={busy === s.id} onClick={() => void sendBack(s)}>
                              Send back
                            </Btn>
                            <input className="cell" placeholder="note" value={notes[s.id] ?? ''} onChange={(e) => setNotes({ ...notes, [s.id]: e.target.value })} style={{ width: '100%' }} />
                          </div>
                        ) : (
                          <Tag kind="un">waiting</Tag>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

/** The library's suggestions (no job): shown in the Library's Engineering queue, approved the same way; an approved row lands unverified. */
export function LibrarySuggestions() {
  const s = useSession();
  const all = useRows('suggestion', { order: ['suggested_at'] });
  const users = useRows('user');
  const operations = useRows('operation');
  const materials = useRows('material');
  const catalog = useRows('catalog_item');
  const recipes = useRows('recipe');
  // the rows a catalog suggestion changes (2026-10-18), so the queue can say what each holds now
  const steps = useRows('recipe_route_step');
  const lines = useRows('recipe_bom_line');
  const parts = useRows('catalog_part');
  const inputs = useRows('catalog_input');
  const calcs = useRows('catalog_derived');
  const rows = all.rows.filter((x) => x.job_id === null);
  const waiting = rows.filter((x) => x.status === 'waiting');
  const decided = rows.filter((x) => x.status !== 'waiting').sort((a, b) => String(b.decided_at).localeCompare(String(a.decided_at)));
  const none = () => null;
  const groupOf = (x: Row) => {
    if (x.target_table === 'operation' || x.target_table === 'time_standard') return operations.rows.find((o) => o.id === x.target_id)?.name ?? 'Operation';
    if (x.kind === 'new_material') return 'New materials';
    const p = (x.payload ?? {}) as { recipe_id?: string; catalog_item_id?: string; item_name?: string; recipe_name?: string };
    const recipe = recipes.rows.find((rc) => rc.id === p.recipe_id);
    if (recipe) return `${catalog.rows.find((c) => c.id === recipe.catalog_item_id)?.name ?? p.item_name ?? 'Catalog item'} · ${recipe.name}`;
    if (p.catalog_item_id) return catalog.rows.find((c) => c.id === p.catalog_item_id)?.name ?? p.item_name ?? 'Catalog item';
    return 'Library';
  };
  /** The catalog editor, on the recipe and the row a suggestion is about. */
  const hrefOf = (x: Row): string | null => {
    const p = (x.payload ?? {}) as { recipe_id?: string; catalog_item_id?: string; change?: string };
    if (!p.catalog_item_id) return null;
    const q = new URLSearchParams();
    if (p.recipe_id) q.set('recipe', p.recipe_id);
    if (p.recipe_id && p.change !== 'add' && (x.kind === 'library_route_step' || x.kind === 'library_bom_line') && x.target_id) q.set('row', String(x.target_id));
    return `/library/catalog/${p.catalog_item_id}${q.size ? `?${q}` : ''}`;
  };
  /** What a catalog row holds now, as ug.suggestion_current_value reads it; undefined where the database is the one to say (an add, a list). */
  const currentOf = (x: Row): string | null | undefined => {
    if (!isCatalogKind(x.kind)) return undefined;
    const p = (x.payload ?? {}) as { change?: string };
    if (p.change === 'add') return undefined;
    const table = { library_route_step: steps, library_bom_line: lines, library_part: parts, library_input: inputs, library_calculation: calcs }[String(x.kind)];
    const row = table?.rows.find((r) => r.id === x.target_id);
    if (table?.loading) return undefined;
    if (!row) return '(gone)';
    if (p.change === 'remove') {
      const opName = (id: unknown) => String(operations.rows.find((o) => o.id === id)?.name ?? '(operation removed)');
      const matName = (id: unknown) => (id ? String(materials.rows.find((m) => m.id === id)?.description ?? '(material removed)') : null);
      switch (x.kind) {
        case 'library_route_step':
          return `${opName(row.operation_id)} × ${String(row.qty_expr)}`;
        case 'library_bom_line':
          return `${matName(row.material_id) ?? (row.label || row.material_role || '(line)')} × ${String(row.qty_expr)}`;
        case 'library_part':
          return `${String(row.name)} × ${String(row.qty_expr)}, ${String(row.length_expr)} × ${String(row.width_expr)}`;
        case 'library_input':
          return `${String(row.key)} (${String(row.data_type)}${row.default_value !== null && row.default_value !== undefined ? `, default ${String(row.default_value)}` : ''})`;
        default:
          return `${String(row.key)} = ${String(row.expr)}`;
      }
    }
    const v = row[String(x.target_key)];
    if (v !== null && typeof v === 'object') return undefined;
    return v === null || v === undefined || v === '' ? null : String(v);
  };
  return (
    <Panel label={`Library suggestions · ${waiting.length} waiting`} soft={waiting.length > 0} right={<Info left>What an AI employee suggests for the library: a catalog item's recipe step, material line or part, its input or calculation (added, changed or removed), a recipe line's formula, an operation, a time standard, or a new material. From a login at Suggest on Library, nothing reaches the library until you approve it here; a login at Change writes directly, the row it touched goes unverified, and the edit shows in the history below as "changed directly" (a removal always waits here). Approve as shown applies exactly what is in the box; an edit returns the recipe to unverified until you verify it on the item (a note does not); a removal deletes the row. Send back returns it with your note. The heading opens the item in the editor.</Info>}>
      <SuggestionRows rows={waiting} users={users.rows} canDecide={s.canEdit('screen.library')} groupOf={groupOf} numberOf={none} labelOf={none} currentOf={currentOf} hrefOf={hrefOf} widths={['20%', '24%', '26%', '16%', '14%']} />
      {decided.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary>History · {decided.length}</summary>
          <SuggestionHistory rows={decided} users={users.rows} numberOf={none} labelOf={none} />
        </details>
      )}
    </Panel>
  );
}

/** The decided rows: the history. */
export function SuggestionHistory({ rows, users, numberOf, labelOf, addOnName }: { rows: Row[]; users: Row[]; numberOf: (id: unknown) => string | null; labelOf: (itemId: unknown, key: string) => string | null; addOnName?: (id: unknown) => string | null }) {
  const who = (id: unknown) => users.find((u) => u.id === id)?.name ?? '—';
  if (rows.length === 0) return <Empty>No history yet.</Empty>;
  return (
    <div className="tblwrap">
      <table>
        <thead>
          <tr>
            <th>What changed</th>
            <th>From → to</th>
            <th>Suggested</th>
            <th>Decided</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td>
                {whatChanges(s, numberOf, labelOf, addOnName)}
                <br />
                <small>{s.reason} · {String(s.source).slice(0, 60)}</small>
              </td>
              <td className="nowrap">
                {show(s.old_value)} → {s.status === 'approved' || s.status === 'applied' ? show(s.applied_value) : show(s.new_value)}
                {s.status === 'approved' && show(s.applied_value) !== show(s.new_value) && <small> (suggested {show(s.new_value)})</small>}
              </td>
              <td className="nowrap">
                {who(s.suggested_by)}
                <br />
                <small>{longDate(s.suggested_at)}</small>
              </td>
              <td className="nowrap">
                {s.status === 'approved' ? <Tag kind="on">approved</Tag> : s.status === 'applied' ? <Tag kind="un" title="Written directly by a login whose row of the grid says Change; unverified until you verify the row it touched">changed directly</Tag> : s.status === 'withdrawn' ? <Tag kind="un" title="Taken back by the one who suggested it, before a decision">withdrawn</Tag> : <Tag kind="off" title={s.applied_value !== null && s.applied_value !== undefined ? 'Sent back after it was applied: the row went back to the version you verified' : undefined}>sent back</Tag>} {who(s.decided_by)}
                <br />
                <small>{longDate(s.decided_at)}</small>
              </td>
              <td>{s.note ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Suggestions({ ctx }: { ctx: JobCtx }) {
  const { data, r, jobId } = ctx;
  const s = useSession();
  const rows = useRows('suggestion', { eq: { job_id: jobId }, order: ['suggested_at'] });
  const handoffs = useRows('handoff', { eq: { job_id: jobId }, order: ['filed_at'] });
  const users = useRows('user');
  const defs = useRows('standard_def');
  const res = r.res!;
  // a scope-row suggestion waits on the Scope tab (2026-10-18); decided, it is history here like any other
  const scopeWaiting = rows.rows.filter((x) => x.status === 'waiting' && isScopeKind(x.kind));
  const waiting = rows.rows.filter((x) => x.status === 'waiting' && !isScopeKind(x.kind));
  const decided = rows.rows.filter((x) => x.status !== 'waiting').sort((a, b) => String(b.decided_at).localeCompare(String(a.decided_at)));
  const canDecide = s.canEdit('screen.jobs') && !ctx.locked;
  const forMe = handoffs.rows.filter((x) => x.status === 'waiting');
  const forMeClosed = handoffs.rows.filter((x) => x.status !== 'waiting').sort((a, b) => String(b.closed_at).localeCompare(String(a.closed_at)));

  const numberOf = (id: unknown) => res.items.find((i) => i.id === id)?.number ?? data.items.find((i) => i.id === id)?.number ?? null;
  const addOnName = (id: unknown) => data.addOns.find((a) => a.id === id)?.name ?? null;
  // an alternate starts as the item it replaces: say only what it changes, by the input's label
  const alternateDetail = (x: Row): string | null => {
    if (x.kind !== 'create_add_on') return null;
    const alts = (((x.payload ?? {}) as { alternates?: Row[] }).alternates ?? []).map((a) => {
      const base = data.items.find((i) => i.id === a.replaces_item_id);
      const cat = a.catalog_item_id ? r.lib?.catalog[a.catalog_item_id] : undefined;
      const changed = Object.entries((a.inputs ?? {}) as Record<string, unknown>)
        .filter(([k, v]) => show(v) !== show((base?.inputs ?? {})[k]))
        .map(([k, v]) => `${cat?.inputs.find((i) => i.key === k)?.label ?? k} ${show(v)}`);
      const swapped = base && a.catalog_item_id !== base.catalog_item_id ? ` as ${cat?.name ?? 'another catalog item'}` : '';
      return `${a.number}${swapped}: ${changed.length ? changed.join(', ') : 'as the item it replaces'}`;
    });
    return alts.length ? alts.join(' · ') : null;
  };
  const labelOf = (itemId: unknown, key: string) => res.items.find((i) => i.id === itemId)?.inputs.find((x) => x.key === key)?.label ?? null;
  const currentOf = (x: Row): string | null | undefined => {
    switch (x.kind) {
      case 'change_input':
      case 'type_price': {
        if (x.target_table === 'sub_line') {
          const line = data.subLines.find((l) => l.id === x.target_id);
          // an amount as a number, the way ug.same_value reads it: 898 and 898.00 are the same (2026-10-25)
          return line ? (line[String(x.target_key)] === null || line[String(x.target_key)] === undefined ? null : String(Number(line[String(x.target_key)]))) : null;
        }
        const item = data.items.find((i) => i.id === x.target_id);
        if (!item) return null;
        const v = (item.inputs ?? {})[String(x.target_key)];
        return v === undefined || v === null ? null : String(v);
      }
      case 'set_job_standard': {
        const defId = defs.rows.find((d) => d.key === x.target_key)?.id;
        const row = data.jobStandards.find((js) => js.standard_def_id === defId);
        return row ? (row.value === null ? null : String(row.value)) : null;
      }
      case 'move_item': {
        const item = data.items.find((i) => i.id === x.target_id);
        return item ? String(item.add_on_id === ((x.payload ?? {}) as { add_on_id?: string }).add_on_id) : null;
      }
      case 'custom_line':
        return undefined;
      case 'hold_value': {
        const item = data.items.find((i) => i.id === x.target_id);
        if (!item) return null;
        const v = (item.overrides ?? {})[String(x.target_key)];
        return v === undefined || v === null ? null : String(v);
      }
      case 'item_route_step':
      case 'item_bom_line': {
        // the same reading as ug.suggestion_current_value: an own line by index (what it is and how much), a recipe row's formula
        const p = (x.payload ?? {}) as { change?: string; at?: number | null; step_id?: string | null; line_id?: string | null };
        if (p.change === 'add') return undefined;
        const item = data.items.find((i) => i.id === x.target_id);
        if (!item) return '(gone)';
        if (typeof p.at === 'number') {
          const lines = ((x.kind === 'item_route_step' ? item.custom_route : item.custom_bom) ?? []) as Record<string, unknown>[];
          const l = lines[p.at];
          return l ? `${String(l.operation_id ?? l.material_id ?? l.label ?? '')}|${l.qty === null || l.qty === undefined ? '' : String(l.qty)}` : '(gone)';
        }
        const cat = item.catalog_item_id ? r.lib?.catalog[item.catalog_item_id] : undefined;
        for (const rc of cat?.recipes ?? []) {
          const row = x.kind === 'item_route_step' ? rc.route.find((st) => st.id === p.step_id) : rc.bom.find((b) => b.id === p.line_id);
          if (row) return row.qty_expr;
        }
        return '(gone)';
      }
      case 'new_material': {
        const want = String(((x.payload ?? {}) as { description?: string }).description ?? '').trim().toLowerCase();
        const same = Object.values(r.lib?.materials ?? {}).find((m) => m.description.trim().toLowerCase() === want);
        return same ? same.description : null;
      }
      case 'job_route_step':
      case 'job_bom_line': {
        // the same reading as ug.job_line_current: the job's own row for the step or line, else the type's starting quantity, as a plain number
        const p = (x.payload ?? {}) as { change?: string; line_id?: string | null; type_step_id?: string | null; type_line_id?: string | null };
        if (p.change === 'add') return undefined;
        const route = x.kind === 'job_route_step';
        const own = route ? data.routeLines : data.bomLines;
        const typeRows = route ? data.typeSteps : data.typeBomLines;
        const typeKey = route ? 'type_step_id' : 'type_line_id';
        const typeId = route ? p.type_step_id : p.type_line_id;
        const num = (v: unknown) => (v === null || v === undefined || v === '' ? null : String(Number(v)));
        const row = (p.line_id && own.find((l) => l.id === p.line_id)) || (typeId && own.find((l) => l[typeKey] === typeId));
        if (row) return num(row.qty);
        const t = typeId ? typeRows.find((tr) => tr.id === typeId) : undefined;
        return t ? num(t.qty) : '(gone)';
      }
      case 'job_cost_line': {
        const p = (x.payload ?? {}) as { change?: string; line_id?: string | null };
        if (p.change === 'add') return undefined;
        const c = data.costLines.find((l) => l.id === p.line_id);
        return c ? (c.amount === null || c.amount === undefined ? null : String(Number(c.amount))) : '(gone)';
      }
      case 'create_add_on': {
        // what stops it: an item to move gone or already in an add-on; nothing stops it reads blank
        const p = (x.payload ?? {}) as { move_item_ids?: string[]; alternates?: Row[] };
        for (const id of p.move_item_ids ?? []) {
          const item = data.items.find((i) => i.id === id);
          if (!item) return 'an item to move is no longer on the job';
          if (item.add_on_id) return `${item.number} is already in an add-on`;
        }
        for (const a of p.alternates ?? []) if (a.replaces_item_id && !data.items.some((i) => i.id === a.replaces_item_id)) return 'an item it replaces is no longer on the job';
        return null;
      }
      default:
        return undefined;
    }
  };
  const groupOf = (x: Row) =>
    x.target_table === 'job_item' && x.target_id ? `${numberOf(x.target_id) ?? 'Item'}` : x.kind === 'add_job_item' || x.kind === 'custom_line' ? 'New items' : x.kind === 'create_add_on' ? 'Add-ons' : x.kind === 'new_material' ? 'Library' : x.target_table === 'sub_line' ? 'Sub lines' : x.kind === 'job_route_step' || x.kind === 'job_bom_line' ? 'Job route' : x.kind === 'job_cost_line' ? 'Job-wide costs' : 'The job';

  // price if all approved: the engine over the job with every waiting suggestion laid on, never stored
  const preview = useMemo(() => {
    if (!r.lib || !r.jobIn) return null;
    const applied = applySuggestions(r.jobIn, waiting as unknown as SuggestionIn[]);
    try {
      // a new material waiting beside a line that buys it: the preview prices the line from the suggestion
      return { ...applied, res: resolveJob(libraryWith(r.lib, applied), applied.jobIn) };
    } catch (e) {
      return { ...applied, res: null, error: e instanceof Error ? e.message : String(e) };
    }
  }, [r.lib, r.jobIn, waiting]);

  return (
    <div className="stack">
      <div className="tiles">
        <div className="tile">
          <div className="big">
            <Money cents={res.totals.price_cents} />
          </div>
          <small>Price now</small>
        </div>
        <div className={`tile${waiting.length ? ' soft' : ''}`}>
          <div className="big">{preview?.res ? <Money cents={preview.res.totals.price_cents} /> : '—'}</div>
          {preview?.res && waiting.length > 0 && preview.res.totals.price_cents !== res.totals.price_cents && (
            // the difference, signed and on its own line so the sign never parts from the figure, so it is read and not subtracted (Iris, 2026-10-19)
            <div>
              <small className="muted nowrap">
                <Money cents={preview.res.totals.price_cents - res.totals.price_cents} sign /> against now
              </small>
            </div>
          )}
          <small>
            Price if all {waiting.length} approved
            {preview && preview.skipped.length > 0 && ` · ${preview.skipped.length} not in the preview`}
            <Info left>The engine run over the job with every waiting suggestion laid over its rows. Nothing is stored. A step or line suggested on one item, the job route's hours and lines, a job-wide cost, a held value and a new material a line buys are in it; a library recipe suggestion changes the library, not this job, so it is not; moving an item into an add-on changes the add-on.</Info>
          </small>
        </div>
        <div className="tile">
          <div className="big">{waiting.length + forMe.length}</div>
          <small>
            Waiting on you{forMe.length ? ` · ${forMe.length} for you alone` : ''}
          </small>
        </div>
      </div>
      <ForSpencer ctx={ctx} rows={forMe} closed={forMeClosed} users={users.rows} canDecide={s.canEdit('screen.jobs')} numberOf={numberOf} />
      <Panel label={`Suggestions waiting · ${waiting.length}`} soft={waiting.length > 0} right={<Info left>Approve as shown applies exactly what is in the box: the suggested value, or your edit of it. Send back returns it with your note; the suggester reads the note and may suggest again. A suggester may also take a waiting row back itself, with a note saying what changed: it shows in the history as withdrawn. A row whose value has moved since it was suggested cannot be approved until it is suggested again.</Info>}>
        {rows.loading ? <small>Loading…</small> : <SuggestionRows rows={waiting} users={users.rows} canDecide={canDecide} groupOf={groupOf} numberOf={numberOf} labelOf={labelOf} currentOf={currentOf} addOnName={addOnName} detailOf={alternateDetail} />}
        {!canDecide && waiting.length > 0 && <small>Only the owner approves or sends back{ctx.locked ? '; this job is locked' : ''}.</small>}
        {scopeWaiting.length > 0 && (
          <small>
            {scopeWaiting.length} scope-row {scopeWaiting.length === 1 ? 'suggestion waits' : 'suggestions wait'} on the <Link to={`/jobs/${jobId}?tab=scope`}>Scope tab</Link>.
          </small>
        )}
      </Panel>
      <Panel label={`History · ${decided.length}`}>
        <SuggestionHistory rows={decided} users={users.rows} numberOf={numberOf} labelOf={labelOf} addOnName={addOnName} />
      </Panel>
    </div>
  );
}

const pct = (r: Resolution) => `${Number(r.totals.margin_pct).toFixed(1)}%`;

/** For Spencer: what only he can do, filed by an AI employee; Done or Dismiss, each with an optional note. */
function ForSpencer({ ctx, rows, closed, users, canDecide, numberOf }: { ctx: JobCtx; rows: Row[]; closed: Row[]; users: Row[]; canDecide: boolean; numberOf: (id: unknown) => string | null }) {
  const s = useSession();
  const { data, r } = ctx;
  const kinds = s.setting<{ key: string; label: string }[]>('handoff_kinds', []);
  const kindWord = (k: unknown) => kinds.find((x) => x.key === k)?.label ?? String(k);
  const who = (id: unknown) => users.find((u) => u.id === id)?.name ?? '—';
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const waitingSuggestions = useRows('suggestion', { eq: { job_id: ctx.jobId, status: 'waiting' } });

  // the change the employee sent, priced both ways live: the same engine, the same rows, nothing stored
  const bothWays = (w: WhatIf): { asIs: Resolution; withIt: Resolution } | null => {
    if (!r.lib || !r.jobIn) return null;
    try {
      const build = data.builds.find((b) => b.id === w.build_id) ?? null;
      const opts = pricingOptions(build, data.addOns.filter((a) => (w.add_on_ids ?? []).includes(a.id)));
      const applied = applyWhatIf(r.jobIn, w, waitingSuggestions.rows as unknown as SuggestionIn[]);
      return { asIs: resolveJob(r.lib, r.jobIn, opts), withIt: resolveJob(r.lib, applied.jobIn, opts) };
    } catch {
      return null;
    }
  };
  const changeWords = (w: WhatIf) =>
    [
      ...(w.changes ?? []).map((c) => (c.kind === 'input' ? `${c.number ?? numberOf(c.item_id) ?? 'item'} · ${r.res?.items.find((i) => i.id === c.item_id)?.inputs.find((x) => x.key === c.key)?.label ?? c.key} → ${c.value === true ? 'on' : c.value === false ? 'off' : String(c.value)}` : c.kind === 'percentage' ? `${c.key} → ${String(c.value)}%` : `${c.key} → ${String(c.value)}`)),
      ...(w.build_id ? [`under ${data.builds.find((b) => b.id === w.build_id)?.name ?? 'a build no longer on the job'}`] : []),
      ...(w.include_waiting ? ['with the waiting suggestions'] : []),
    ].join(' · ');

  const close = async (h: Row, status: 'done' | 'dismissed') => {
    setBusy(h.id);
    try {
      await rpc('close_handoff', { p_id: h.id, p_status: status, p_note: notes[h.id]?.trim() || null });
      toast(status === 'done' ? 'Done.' : 'Dismissed.');
      refresh('handoff');
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (rows.length === 0 && closed.length === 0) return null;
  return (
    <Panel
      label={`For Spencer · ${rows.length} waiting`}
      soft={rows.length > 0}
      right={<Info left>What an AI employee hands you that a suggestion cannot reach: the job's percentages, the large-purchase switch, a build, publishing, verifying, a call only you can make. Open the link, do it there, then tick Done (or Dismiss), with a note if you like; the employee reads both. Where the employee sent the change itself, the price and the margin are worked out both ways from the job as it stands now, and nothing is stored.</Info>}
    >
      {rows.length === 0 && <Empty>Nothing waiting.</Empty>}
      <div className="stack">
        {rows.map((h) => {
          const item = h.job_item_id ? numberOf(h.job_item_id) : (h.item_number as string | null);
          const both = h.what_if ? bothWays(h.what_if as WhatIf) : null;
          return (
            <div key={h.id} className="card" style={{ padding: 10 }}>
              <div>
                <Tag kind="un">{kindWord(h.kind)}</Tag> {item && <b>{item}</b>} {h.what}
              </div>
              <small>{h.why}</small>
              <div>
                <small>
                  {who(h.filed_by)}, {longDate(h.filed_at)}
                  {h.link && (
                    <>
                      {' · '}
                      <Link to={String(h.link)}>open where it is done</Link>
                    </>
                  )}
                </small>
              </div>
              {h.what_if && (
                <div className="tblwrap" style={{ marginTop: 6 }}>
                  <small>If you do it: {changeWords(h.what_if as WhatIf)}</small>
                  <table className="fixed">
                    <colgroup>
                      <col style={{ width: '24%' }} />
                      <col style={{ width: '38%' }} />
                      <col style={{ width: '38%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th></th>
                        <th className="right">As it is</th>
                        <th className="right">If you do it</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Price</td>
                        <td className="right">{both ? <Money cents={both.asIs.totals.price_cents} /> : '—'}</td>
                        <td className="right">{both ? <Money cents={both.withIt.totals.price_cents} /> : '—'}</td>
                      </tr>
                      <tr>
                        <td>Margin</td>
                        <td className="right">{both ? <><Money cents={both.asIs.totals.margin_cents} /><br /><small>{pct(both.asIs)}</small></> : '—'}</td>
                        <td className="right">{both ? <><Money cents={both.withIt.totals.margin_cents} /><br /><small>{pct(both.withIt)}</small></> : '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {canDecide ? (
                <div className="row mid" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  <Btn small primary disabled={busy === h.id} onClick={() => void close(h, 'done')}>
                    Done
                  </Btn>
                  <Btn small disabled={busy === h.id} onClick={() => void close(h, 'dismissed')}>
                    Dismiss
                  </Btn>
                  <input className="cell grow" placeholder="note (optional)" value={notes[h.id] ?? ''} onChange={(e) => setNotes({ ...notes, [h.id]: e.target.value })} style={{ minWidth: 160 }} />
                </div>
              ) : (
                <small>Only the owner closes these.</small>
              )}
            </div>
          );
        })}
      </div>
      {closed.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary>Closed · {closed.length}</summary>
          <div className="tblwrap">
            <table>
              <thead>
                <tr>
                  <th>What</th>
                  <th>Filed</th>
                  <th>Closed</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {closed.map((h) => (
                  <tr key={h.id}>
                    <td>
                      {kindWord(h.kind)} · {h.job_item_id ? numberOf(h.job_item_id) ?? '' : h.item_number ?? ''} {h.what}
                    </td>
                    <td className="nowrap">
                      {who(h.filed_by)}
                      <br />
                      <small>{longDate(h.filed_at)}</small>
                    </td>
                    <td className="nowrap">
                      {h.status === 'done' ? <Tag kind="on">done</Tag> : <Tag kind="off">dismissed</Tag>} {who(h.closed_by)}
                      <br />
                      <small>{longDate(h.closed_at)}</small>
                    </td>
                    <td>{h.note ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </Panel>
  );
}
